import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { bumpInteraction } from "../lib/recommendations";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("prize_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  // When user logs in, sync server items into local (and push any local-only ones up)
  const syncFromServer = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/wishlist");
      const serverItems = r.data || [];
      // push local-only items to server
      const localOnly = (items || []).filter((li) => !serverItems.find((si) => si.id === li.id));
      for (const li of localOnly) {
        try { await api.post("/wishlist", { product_id: li.id }); } catch {}
      }
      const r2 = await api.get("/wishlist");
      setItems(r2.data || []);
    } catch {}
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (user) syncFromServer();
  }, [user, syncFromServer]);

  useEffect(() => {
    localStorage.setItem("prize_wishlist", JSON.stringify(items));
  }, [items]);

  const toggle = async (product) => {
    const exists = items.find((p) => p.id === product.id);
    // Both add and remove are strong intent signals for the affinity engine —
    // bump the per-session counter so subsequent recommendation calls reflect
    // a more engaged shopper.
    bumpInteraction();
    if (exists) {
      setItems((prev) => prev.filter((p) => p.id !== product.id));
      if (user) { try { await api.delete(`/wishlist/${product.id}`); } catch {} }
    } else {
      setItems((prev) => [...prev, product]);
      if (user) { try { await api.post("/wishlist", { product_id: product.id }); } catch {} }
    }
  };
  const remove = async (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    if (user) { try { await api.delete(`/wishlist/${id}`); } catch {} }
  };
  const has = (id) => items.some((p) => p.id === id);

  return (
    <WishlistContext.Provider value={{ items, toggle, remove, has, count: items.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
